
import { useState, useEffect, useCallback } from 'react';

interface CaptchaFieldProps {
  /** Callback fired when the captcha verification status changes */
  onVerified: (verified: boolean) => void;
}

/**
 * Generates a simple addition captcha.
 * Returns both the human‑readable question and the numeric answer.
 */
const generateCaptcha = (): { question: string; answer: number } => {
  const a = Math.floor(Math.random() * 10) + 1;
  const b = Math.floor(Math.random() * 10) + 1;
  return { question: `${a} + ${b} = ?`, answer: a + b };
};

const CaptchaField = ({ onVerified }: CaptchaFieldProps) => {
  const [captcha, setCaptcha] = useState(generateCaptcha());
  const [userAnswer, setUserAnswer] = useState('');
  const [status, setStatus] = useState<'idle' | 'correct' | 'wrong'>('idle');

  /** Refreshes the captcha and resets all related state. */
  const refreshCaptcha = useCallback(() => {
    setCaptcha(generateCaptcha());
    setUserAnswer('');
    setStatus('idle');
    // Notify parent that the captcha is now unverified.
    try {
      onVerified(false);
    } catch (e) {
      console.error('onVerified callback failed:', e);
    }
  }, [onVerified]);

  /**
   * Validate the answer whenever the user input or the captcha changes.
   * The effect also keeps the parent component in sync via `onVerified`.
   */
  useEffect(() => {
    // Empty input – treat as idle.
    if (userAnswer === '') {
      setStatus('idle');
      try {
        onVerified(false);
      } catch (e) {
        console.error('onVerified callback failed:', e);
      }
      return;
    }

    const parsed = parseInt(userAnswer, 10);

    if (!isNaN(parsed) && parsed === captcha.answer) {
      setStatus('correct');
      try {
        onVerified(true);
      } catch (e) {
        console.error('onVerified callback failed:', e);
      }
    } else if (userAnswer.length >= String(captcha.answer).length) {
      // User has entered enough digits but it's still wrong.
      setStatus('wrong');
      try {
        onVerified(false);
      } catch (e) {
        console.error('onVerified callback failed:', e);
      }
    } else {
      setStatus('idle');
      try {
        onVerified(false);
      } catch (e) {
        console.error('onVerified callback failed:', e);
      }
    }
  }, [userAnswer, captcha.answer, onVerified]);

  return (
    <div className="mb-6">
      <label className="block text-sm font-semibold text-gray-900 mb-2">
        Security Verification *
      </label>

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 px-4 py-3 bg-gray-100 rounded-lg border border-gray-200 select-none">
          <i className="ri-shield-check-line text-primary-600"></i>
          <span className="text-sm font-bold text-gray-800 tracking-wider">
            {captcha.question}
          </span>
        </div>

        <input
          type="text"
          inputMode="numeric"
          value={userAnswer}
          onChange={(e) =>
            setUserAnswer(e.target.value.replace(/[^0-9]/g, ''))
          }
          placeholder="Answer"
          required
          className={`w-24 px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent text-sm text-center font-semibold ${
            status === 'correct'
              ? 'border-green-400 focus:ring-green-500 bg-green-50'
              : status === 'wrong'
              ? 'border-red-400 focus:ring-red-500 bg-red-50'
              : 'border-gray-300 focus:ring-primary-600'
          }`}
        />

        <button
          type="button"
          onClick={refreshCaptcha}
          className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
          aria-label="Refresh captcha"
        >
          <i className="ri-refresh-line text-gray-600"></i>
        </button>

        {status === 'correct' && (
          <div className="w-6 h-6 flex items-center justify-center">
            <i className="ri-check-line text-green-600 text-lg"></i>
          </div>
        )}
      </div>

      {status === 'wrong' && (
        <p className="text-xs text-red-600 mt-1">
          Incorrect answer. Please try again.
        </p>
      )}
    </div>
  );
};

export default CaptchaField;
